import { useState } from 'react';
import { FaUserCircle, FaUsers } from 'react-icons/fa';
import { IoSend } from 'react-icons/io5';
import './CommentPopup.scss';

export const FILTER_OPTIONS = [
  'All',
  'Correction Direction',
  'Target 1 Hit Possible',
  'Target 2 Hit Possible',
  'Stoploss Hit Possible',
  'No Trade Zone',
];

export const PREDEFINED_TAGS = [
  'Correction Direction',
  'No Trade Zone',
  'Target 1 Hit Possible',
  'Stoploss Hit Possible',
  'Target 2 Hit Possible',
];

export const TAG_CLASS = {
  'Correction Direction': 'tag-correction',
  'Target 1 Hit Possible': 'tag-target1',
  'Target 2 Hit Possible': 'tag-target2',
  'Stoploss Hit Possible': 'tag-stoploss',
  'No Trade Zone': 'tag-notrade',
};

export const MOCK_COMMENTS = [
  { id: 1, author: 'Edward', time: '5m', tag: 'Correction Direction', text: 'Weak structure + resistance rejection + high chances of stoploss getting hit.' },
  { id: 2, author: 'Julia', time: '15m', tag: 'Target 1 Hit Possible', text: 'Stoploss hunters are about to feast' },
  { id: 3, author: 'Rizwan', time: '8hr', tag: 'Target 2 Hit Possible', text: 'Looks like Bitcoin is heading straight for the stoploss' },
  { id: 4, author: 'Monika', time: '10hr', tag: 'Stoploss Hit Possible', text: 'Momentum fading, stoploss seems inevitable from here' },
];

/**
 * CommentTrigger — the 👥 count button that opens the popup.
 * Usage: <CommentTrigger count={comments.length} onClick={() => setOpen(true)} />
 */
export function CommentTrigger({ count, onClick }) {
  return (
    <div className="comment-trigger" onClick={onClick}>
      <FaUsers className="comment-trigger-icon" />
      <span className="comment-trigger-count">{count}</span>
    </div>
  );
}

/**
 * CommentPopup — reusable comment popup.
 *
 * Props:
 *   open          {boolean}   whether the popup is visible
 *   onClose       {function}  called when popup should close
 *   comments      {array}     comment list managed by parent
 *   onAddComment  {function}  called with new comment object; parent updates its state
 */
export default function CommentPopup({ open, onClose, comments = [], onAddComment }) {
  const [commentFilter, setCommentFilter] = useState('All');
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [selectedTag, setSelectedTag] = useState('');
  const [commentText, setCommentText] = useState('');

  if (!open) return null;

  const filteredComments = commentFilter === 'All'
    ? comments
    : comments.filter(c => c.tag === commentFilter);

  const handleSend = () => {
    const text = commentText.trim();
    if (!selectedTag && !text) return;
    onAddComment({ id: Date.now(), author: 'You', time: 'Just now', tag: selectedTag, text });
    setSelectedTag('');
    setCommentText('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSend();
  };

  const handleOverlayClick = () => {
    setShowFilterDropdown(false);
    onClose();
  };

  return (
    <div className="comment-overlay" onClick={handleOverlayClick}>
      <div className="comment-popup" onClick={e => e.stopPropagation()}>

        {/* HEADER */}
        <div className="comment-header">
          <div className="comment-filter-wrap">
            <button
              className="comment-filter-btn"
              onClick={() => setShowFilterDropdown(v => !v)}
            >
              {commentFilter} <span className="filter-arrow">▾</span>
            </button>
            {showFilterDropdown && (
              <div className="filter-dropdown">
                {FILTER_OPTIONS.map(opt => (
                  <div
                    key={opt}
                    className={`filter-option${commentFilter === opt ? ' active' : ''}`}
                    onClick={() => { setCommentFilter(opt); setShowFilterDropdown(false); }}
                  >
                    {opt}
                  </div>
                ))}
              </div>
            )}
          </div>
          <button className="comment-close-btn" onClick={onClose}>✕</button>
        </div>

        {/* COMMENT LIST */}
        <div className="comment-list">
          {filteredComments.length === 0 && (
            <div className="comment-empty">No comments yet.</div>
          )}
          {filteredComments.map(comment => (
            <div key={comment.id} className="comment-item">
              <FaUserCircle className="comment-avatar" />
              <div className="comment-body">
                <div className="comment-meta">
                  <span className="comment-author">{comment.author}</span>
                  <span className="comment-time">{comment.time}</span>
                </div>
                {comment.tag && (
                  <span className={`comment-tag ${TAG_CLASS[comment.tag] || ''}`}>{comment.tag}</span>
                )}
                <div className="comment-text">{comment.text}</div>
              </div>
            </div>
          ))}
        </div>

        {/* INPUT AREA */}
        <div className="comment-input-area">
          <div className="comment-input-wrapper">
            {selectedTag && (
              <span className={`input-tag-pill ${TAG_CLASS[selectedTag] || ''}`}>
                {selectedTag}
                <button className="remove-tag" onClick={() => setSelectedTag('')}>✕</button>
              </span>
            )}
            <input
              className="comment-input"
              type="text"
              placeholder={selectedTag ? 'Add your comment...' : 'Share your thought about this signal...'}
              value={commentText}
              onChange={e => setCommentText(e.target.value)}
              onKeyDown={handleKeyDown}
            />
          </div>
          <button className="send-btn" onClick={handleSend}>
            <IoSend />
          </button>
        </div>

        {/* PREDEFINED TAGS */}
        <div className="predefined-tags">
          {PREDEFINED_TAGS.map(tag => (
            <button
              key={tag}
              className={`predefined-tag-btn${selectedTag === tag ? ' active' : ''}`}
              onClick={() => setSelectedTag(prev => prev === tag ? '' : tag)}
            >
              {tag}
            </button>
          ))}
        </div>

      </div>
    </div>
  );
}
